/**
 * features/hr/LeaveEntitlements.tsx
 * ------------------------------------------------------------------
 * HR LEAVE — entitlement settings. For a chosen leave year, configure
 * the day entitlement per leave type for each cohort (staff category).
 * These become the cohort default; a per-staff allocation can still
 * override an individual. Renders embedded in the Leave hub.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { SlidersHorizontal, Save } from 'lucide-react';
import { SoftCard, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { STAFF_CATEGORY_LABELS, LEAVE_TYPE_LABELS, type StaffCategory } from '@/lib/hr';
import { DEFAULT_LEAVE_ENTITLEMENTS } from '@/lib/hrPayroll';
import { ENTITLEMENT_LEAVE_TYPES, findPolicy, currentLeaveYear } from '@/lib/leavePolicy';
import { useLeavePolicies, saveLeavePolicy } from '@/features/hr/useLeavePolicies';

type Draft = Record<string, Record<string, string>>; // category -> type -> value(string)

export default function LeaveEntitlements({ canEdit }: { canEdit: boolean }) {
  const { policies, loading } = useLeavePolicies();
  const thisYear = Number(currentLeaveYear());
  const [year, setYear] = useState(String(thisYear));
  const [draft, setDraft] = useState<Draft>({});
  const [savingCat, setSavingCat] = useState<StaffCategory | null>(null);
  const [save, setSave] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const categories = useMemo(() => Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[], []);

  // Seed the editable grid from saved policies whenever year/policies change.
  useEffect(() => {
    const next: Draft = {};
    for (const cat of categories) {
      const ent = findPolicy(policies, year, cat)?.entitlements ?? {};
      next[cat] = {};
      for (const t of ENTITLEMENT_LEAVE_TYPES) {
        const v = ent[t];
        next[cat][t] = v != null ? String(v) : '';
      }
    }
    setDraft(next);
  }, [policies, year, categories]);

  function setCell(cat: StaffCategory, type: string, value: string) {
    setDraft((d) => ({ ...d, [cat]: { ...d[cat], [type]: value.replace(/[^0-9]/g, '') } }));
  }

  async function saveRow(cat: StaffCategory) {
    setSavingCat(cat);
    setSave({ show: true, status: 'saving', message: `Saving ${STAFF_CATEGORY_LABELS[cat]} entitlements` });
    try {
      const entitlements: Record<string, number> = {};
      for (const t of ENTITLEMENT_LEAVE_TYPES) {
        const raw = draft[cat]?.[t];
        if (raw !== undefined && raw !== '') entitlements[t] = Number(raw);
      }
      await saveLeavePolicy(year, cat, entitlements);
      setSave({ show: true, status: 'success', message: `${STAFF_CATEGORY_LABELS[cat]} entitlements saved for ${year}.` });
    } catch (e) {
      setSave({ show: true, status: 'error', message: e instanceof Error ? e.message : 'Save failed' });
    } finally {
      setSavingCat(null);
    }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner label="Loading entitlements…" /></div>;

  const yearOptions = [thisYear + 1, thisYear, thisYear - 1];

  return (
    <div className="space-y-4">
      <SaveConfirmation show={save.show} status={save.status} message={save.message} onDismiss={() => setSave((s) => ({ ...s, show: false }))} />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <SlidersHorizontal size={16} className="text-[var(--accent-link,#2d5295)]" />
          Day entitlements per cohort · leave year
        </div>
        <select value={year} onChange={(e) => setYear(e.target.value)} className={glassFieldClass('py-1.5 text-sm')}>
          {yearOptions.map((y) => <option key={y} value={String(y)}>{y}</option>)}
        </select>
      </div>

      <SoftCard hover={false}>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
          Blank uses the system default ({DEFAULT_LEAVE_ENTITLEMENTS.annual} annual / {DEFAULT_LEAVE_ENTITLEMENTS.sick} sick). A
          per-staff allocation still overrides an individual.
        </p>
      </SoftCard>

      <SoftCard pad="p-0" hover={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 px-4 font-medium">Cohort</th>
                {ENTITLEMENT_LEAVE_TYPES.map((t) => (
                  <th key={t} className="py-2 px-3 font-medium text-center">{LEAVE_TYPE_LABELS[t]}</th>
                ))}
                <th className="py-2 px-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {categories.map((cat) => (
                <tr key={cat}>
                  <td className="py-2 px-4 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{STAFF_CATEGORY_LABELS[cat]}</td>
                  {ENTITLEMENT_LEAVE_TYPES.map((t) => (
                    <td key={t} className="py-2 px-3 text-center">
                      <input
                        inputMode="numeric"
                        disabled={!canEdit}
                        value={draft[cat]?.[t] ?? ''}
                        onChange={(e) => setCell(cat, t, e.target.value)}
                        placeholder={t === 'annual' || t === 'sick' ? String(DEFAULT_LEAVE_ENTITLEMENTS[t]) : '—'}
                        className="w-16 text-center rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 px-2 py-1 text-sm disabled:opacity-60"
                      />
                    </td>
                  ))}
                  <td className="py-2 px-4 text-right">
                    {canEdit && (
                      <button
                        onClick={() => saveRow(cat)}
                        disabled={savingCat === cat}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-lg disabled:opacity-50"
                      >
                        <Save size={12} /> Save
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>
    </div>
  );
}
